<script>
    import modalService from '@/services/core/modal.service';
    import Modal from '@/components/modals/Modal';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import Spinner from '@/components/shared/Spinner';
    import opsChangeInboxService from '@/services/opsChangeInbox.service';
    import store from '@/store/global.store';

    export default {
        props: {
            modalData: Object
        },
        components: {
            Modal,
            ButtonSpinner,
            Spinner
        },
        data() {
            return {
                emails: [],
                newEmail: '',
                isLoading: false,
                isSaving: false,
                disablingId: null,
                error: null
            };
        },
        computed: {
            companyId() {
                return this.modalData?.companyId;
            },
            companyName() {
                return this.modalData?.companyName || '';
            },
            companyLabel() {
                return this.companyName || `company ${this.companyId}`;
            },
            isReadOnly() {
                return store.getters.currentUser()?.isReadOnly === true;
            }
        },
        methods: {
            apiError(err, fallback) {
                return err?.data?.message || err?.message || fallback;
            },
            close() {
                modalService.closeModal(true);
            },
            async load() {
                this.isLoading = true;
                this.error = null;
                try {
                    this.emails = (await opsChangeInboxService.listSenders(this.companyId)) || [];
                } catch (err) {
                    this.error = this.apiError(err, 'Could not load the access list.');
                } finally {
                    this.isLoading = false;
                }
            },
            async addEmail() {
                if (this.isReadOnly) return;
                const email = (this.newEmail || '').trim();
                if (!email) {
                    this.error = 'Enter the email address to give access to.';
                    return;
                }
                // Caught here rather than at the server so the reason is specific: a typo in an
                // access list silently means that person's emails are ignored.
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    this.error = `${email} is not a complete email address. Use the address they send from, such as partner@example.com.`;
                    return;
                }
                const exists = this.emails.some(r => (r.email || '').toLowerCase() === email.toLowerCase());
                if (exists) {
                    this.error = `${email} already has access.`;
                    return;
                }

                this.isSaving = true;
                this.error = null;
                try {
                    await opsChangeInboxService.addSender({ companyId: this.companyId, email });
                    this.newEmail = '';
                    await this.load();
                } catch (err) {
                    this.error = this.apiError(err, `Could not give ${email} access.`);
                } finally {
                    this.isSaving = false;
                }
            },
            removeEmail(row) {
                if (this.isReadOnly || !row?.id) return;
                // Removing access is not recoverable from this screen and it silently stops that
                // person's emails from being answered, so it is confirmed rather than undone.
                modalService.confirm({
                    title: 'Remove access',
                    msg: `${row.email} will no longer be able to request changes for ${this.companyLabel}. Emails they send to setup@greasebook.com will go unanswered.`,
                    btnTextOk: 'Remove access',
                    btnTextCancel: 'Keep access',
                    onSuccess: () => this.confirmRemoveEmail(row)
                });
            },
            async confirmRemoveEmail(row) {
                this.disablingId = row.id;
                this.error = null;
                try {
                    await opsChangeInboxService.disableSender(row.id);
                    await this.load();
                } catch (err) {
                    this.error = this.apiError(err, `Could not remove access for ${row.email}.`);
                } finally {
                    this.disablingId = null;
                }
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<template>
    <Modal size-class="modal-md">
        <div class="modal-content">
            <div class="modal-header">
                <h3 class="modal-title">Setup inbox access</h3>
            </div>
            <div class="modal-body">
                <p class="text-muted small mb-3">
                    These addresses may request changes for <strong>{{ companyLabel }}</strong> by emailing
                    setup@greasebook.com. Email from anyone else is turned away.
                </p>

                <div v-if="error" class="alert alert-danger py-2" role="alert">{{ error }}</div>
                <Spinner v-if="isLoading" />

                <ul v-else class="list-group mb-3">
                    <li
                        v-for="row in emails"
                        :key="row.id"
                        class="list-group-item py-2 d-flex justify-content-between align-items-center gap-2"
                    >
                        <span>{{ row.email }}</span>
                        <button
                            v-if="!isReadOnly"
                            class="btn btn-sm btn-outline-secondary"
                            type="button"
                            :aria-label="`Remove access for ${row.email}`"
                            :disabled="disablingId === row.id || isSaving"
                            @click="removeEmail(row)"
                        >
                            <ButtonSpinner v-if="disablingId === row.id" />
                            Remove
                        </button>
                    </li>
                    <li v-if="!emails.length" class="list-group-item text-muted py-2">
                        Nobody has access yet, so every email this company sends to setup@greasebook.com is
                        turned away. Add the first address below.
                    </li>
                </ul>

                <form v-if="!isReadOnly" class="row g-2 align-items-end" @submit.prevent="addEmail">
                    <div class="col">
                        <label class="form-label small mb-1" for="opsWhitelistEmail">Give access to</label>
                        <input
                            id="opsWhitelistEmail"
                            v-model="newEmail"
                            class="form-control"
                            type="email"
                            placeholder="partner@example.com"
                            autocomplete="off"
                        />
                    </div>
                    <div class="col-auto">
                        <button
                            class="btn btn-primary"
                            type="submit"
                            :disabled="isSaving || isLoading || disablingId !== null"
                        >
                            <ButtonSpinner v-if="isSaving" />
                            Add
                        </button>
                    </div>
                </form>
            </div>
            <div class="modal-footer">
                <button class="btn btn-outline-secondary" type="button" @click="close">Close</button>
            </div>
        </div>
    </Modal>
</template>
